import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import usePushNotifications from '../hooks/usePushNotifications'
import AthletePanel from './AthletePanel'
import { notifyCoach } from '../utils/notifyCoach'
import {
  FREE_TRIAL_CLASS_LIMIT,
  hasClassAccess,
  hasOpenGymAccess,
  hasActiveFreeTrial,
  isCoach as profileIsCoach,
  isFreeTrial,
  isFreeTrialExpired
} from '../utils/access'

const GYM_TIME_ZONE = 'America/Chicago'

function dateKeyFromParts(year, month, day) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function toISO(d) {
  return dateKeyFromParts(d.getFullYear(), d.getMonth() + 1, d.getDate())
}

function gymDateKey(value = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: GYM_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(new Date(value))
  const values = Object.fromEntries(parts.map(part => [part.type, part.value]))
  return `${values.year}-${values.month}-${values.day}`
}

function parseCalendarDate(dateKey) {
  const [year, month, day] = dateKey.split('-').map(Number)
  return new Date(year, month - 1, day, 12, 0, 0)
}

function timeInGymMinutes(value) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: GYM_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(new Date(value))
  const values = Object.fromEntries(parts.map(part => [part.type, part.value]))
  return Number(values.hour) * 60 + Number(values.minute)
}

function gymTimeLabel(value) {
  return new Date(value).toLocaleTimeString('en-US', {
    timeZone: GYM_TIME_ZONE,
    hour: 'numeric',
    minute: '2-digit'
  })
}

function formatDate(d) { return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) }
function currentTimeLabel() { return gymTimeLabel(new Date()) }

// Day of week helpers
const DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']
const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const CLASS_TRACKS = ['All Tracks', 'Babes Who Fight Bears', 'Strong & Savage', 'Olympic Weightlifting']
const CLASS_TRACK_BADGES = {
  'Babes Who Fight Bears': 'track-bears',
  'Strong & Savage': 'track-strength',
  'All Tracks': 'track-open'
}

function getDayOfWeek(dateStr) {
  const d = parseCalendarDate(dateStr)
  return DAYS[d.getDay()]
}

const CHECKIN_TIMES = [
  '12:00 AM','1:00 AM','2:00 AM','3:00 AM','4:00 AM','5:00 AM',
  '6:00 AM','7:00 AM','8:00 AM','9:00 AM','10:00 AM','11:00 AM',
  '12:00 PM','1:00 PM','2:00 PM','3:00 PM','4:00 PM','5:00 PM',
  '6:00 PM','7:00 PM','8:00 PM','9:00 PM','10:00 PM','11:00 PM'
]
const DEFAULT_OPEN_GYM_DURATION = 60
const DEFAULT_OPEN_GYM_CAPACITY = 1

function timeInputToLabel(value) {
  if (!value) return ''
  const [h, m] = value.split(':')
  const hr = parseInt(h, 10)
  return `${hr > 12 ? hr - 12 : hr === 0 ? 12 : hr}:${m} ${hr >= 12 ? 'PM' : 'AM'}`
}

function labelToMinutes(value) {
  if (!value) return null
  const match = value.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!match) return null
  let hour = parseInt(match[1], 10)
  const minute = parseInt(match[2], 10)
  const period = match[3].toUpperCase()
  if (period === 'PM' && hour !== 12) hour += 12
  if (period === 'AM' && hour === 12) hour = 0
  return hour * 60 + minute
}

function inputTimeToMinutes(value) {
  if (!value) return null
  const [h, m] = value.split(':')
  return parseInt(h, 10) * 60 + parseInt(m, 10)
}

function minutesToInputTime(minutes) {
  const hour = Math.floor(minutes / 60)
  const minute = minutes % 60
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

function defaultOpenGymSlots() {
  return CHECKIN_TIMES.map(label => {
    const startMinutes = labelToMinutes(label)
    const startTime = minutesToInputTime(startMinutes)
    return {
      id: `default-${startTime}`,
      slot_key: `default-${startTime}`,
      start_time: startTime,
      duration_minutes: DEFAULT_OPEN_GYM_DURATION,
      capacity: DEFAULT_OPEN_GYM_CAPACITY,
      recurrence_days: DAYS.join(','),
      notes: '',
      active: true,
      defaultSlot: true
    }
  })
}

function classWindow(cls, isoDate) {
  if (cls.start_time) {
    const start = timeInGymMinutes(cls.start_time)
    return { start, end: start + (cls.duration_minutes || 60) }
  }
  const start = labelToMinutes(cls.recurrence_time)
  if (start == null) return null
  return { start, end: start + (cls.duration_minutes || 60), label: cls.recurrence_time, date: isoDate }
}

function slotWindow(row) {
  const start = inputTimeToMinutes(row.start_time)
  if (start == null) return null
  return { start, end: start + (row.duration_minutes || 60) }
}

function windowsOverlap(a, b) {
  if (!a || !b) return false
  return a.start < b.end && b.start < a.end
}

function rowRepeatsToday(row, dayOfWeek, isoDate) {
  if (!row.active && row.active !== undefined) return false
  if (row.slot_date || row.block_date) return (row.slot_date || row.block_date) === isoDate
  const days = (row.recurrence_days || '').split(',').map(d => d.trim()).filter(Boolean)
  return days.includes(dayOfWeek)
}

function ClassTrackBadge({ track }) {
  const label = track || 'All Tracks'
  return <span className={`track-badge ${CLASS_TRACK_BADGES[label] || 'track-open'}`}>{label}</span>
}

export default function Schedule({ user, profile }) {
  const [currentDate, setCurrentDate] = useState(() => parseCalendarDate(gymDateKey()))
  const [scheduleView, setScheduleView] = useState('schedule')
  const [oneTimeClasses, setOneTimeClasses] = useState([])
  const [recurringClasses, setRecurringClasses] = useState([])
  const [has247, setHas247] = useState(null)
  const [allMembers, setAllMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [show247, setShow247] = useState(false)
  const [showCoachCheckin, setShowCoachCheckin] = useState(false)
  const [checkinTime, setCheckinTime] = useState('6:00 AM')
  const [toast, setToast] = useState(null)
  const [athletePanel, setAthletePanel] = useState(null)
  const [trialUses, setTrialUses] = useState(0)
  const [openGymSlots, setOpenGymSlots] = useState([])
  const [openGymBookings, setOpenGymBookings] = useState([])
  const [openGymBlocks, setOpenGymBlocks] = useState([])
  const [showOpenGymSlotForm, setShowOpenGymSlotForm] = useState(false)
  const [showOpenGymBlockForm, setShowOpenGymBlockForm] = useState(false)
  const [openGymAvailable, setOpenGymAvailable] = useState(true)
  const isCoach = profileIsCoach(profile)
  const isTrial = isFreeTrial(profile)
  const trialExpired = isFreeTrialExpired(profile)
  const { permission, subscribed, loading: pushLoading, subscribe, unsubscribe } = usePushNotifications(user)
  const canUsePaidClassAccess = hasClassAccess(profile)
  const canUseOpenGymAccess = hasOpenGymAccess(profile)
  const canSignUp = canUsePaidClassAccess || (hasActiveFreeTrial(profile) && trialUses < FREE_TRIAL_CLASS_LIMIT)

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(null), 3000) }

  const iso = toISO(currentDate)
  const dayOfWeek = getDayOfWeek(iso)

  const fetchClasses = useCallback(async () => {
    setLoading(true)

    // Fetch one-time classes for this exact date
    const queryStart = new Date(`${iso}T00:00:00.000Z`)
    queryStart.setUTCDate(queryStart.getUTCDate() - 1)
    const queryEnd = new Date(`${iso}T00:00:00.000Z`)
    queryEnd.setUTCDate(queryEnd.getUTCDate() + 2)

    const { data: oneTimeRows } = await supabase
      .from('classes')
      .select('*, class_signups(athlete_id, checkin_time, profiles(name, avatar_url))')
      .eq('is_247', false)
      .is('recurrence_days', null)
      .gte('start_time', queryStart.toISOString())
      .lt('start_time', queryEnd.toISOString())
      .order('start_time', { ascending: true })
    const oneTime = (oneTimeRows || []).filter(cls => gymDateKey(cls.start_time) === iso)

    // Fetch recurring classes that include today's day of week
    const { data: recurring } = await supabase
      .from('classes')
      .select('*')
      .eq('is_247', false)
      .not('recurrence_days', 'is', null)

    // Fetch 24/7 class
    const { data: c247 } = await supabase
      .from('classes')
      .select('*, class_signups(athlete_id, checkin_time, profiles(name, avatar_url))')
      .eq('is_247', true)
      .limit(1)
      .single()

    // Filter recurring classes to only those that match today's day
    const todayRecurring = (recurring || []).filter(cls => {
      const days = (cls.recurrence_days || '').split(',').map(d => d.trim())
      return days.includes(dayOfWeek)
    })

    // For each matching recurring class, get or create an instance for today
    const recurringWithInstances = await Promise.all(todayRecurring.map(async cls => {
      // Try to get existing instance for today
      let { data: instance } = await supabase
        .from('class_instances')
        .select('*, instance_signups(athlete_id, checkin_time, profiles(name, avatar_url))')
        .eq('class_id', cls.id)
        .eq('instance_date', iso)
        .single()

      // Create instance if it doesn't exist yet
      if (!instance) {
        const { data: newInstance } = await supabase
          .from('class_instances')
          .insert({ class_id: cls.id, instance_date: iso })
          .select('*, instance_signups(athlete_id, checkin_time, profiles(name, avatar_url))')
          .single()
        instance = newInstance
      }

      return { ...cls, instance }
    }))

    setOneTimeClasses(oneTime)
    setRecurringClasses(recurringWithInstances.filter(Boolean))
    setHas247(c247 || null)
    setLoading(false)
  }, [currentDate, iso, dayOfWeek])

  const fetchOpenGym = useCallback(async () => {
    const { data: slots, error: slotsError } = await supabase
      .from('open_gym_slots')
      .select('*')
      .eq('active', true)
      .order('start_time', { ascending: true })

    const { data: bookings, error: bookingsError } = await supabase
      .from('open_gym_bookings')
      .select('*, profiles(name, avatar_url)')
      .eq('booking_date', iso)

    const { data: blocks, error: blocksError } = await supabase
      .from('open_gym_blocks')
      .select('*')
      .eq('active', true)

    if (slotsError || bookingsError || blocksError) {
      setOpenGymAvailable(false)
      setOpenGymSlots([])
      setOpenGymBookings([])
      setOpenGymBlocks([])
      return
    }

    setOpenGymAvailable(true)
    setOpenGymSlots(slots || [])
    setOpenGymBookings(bookings || [])
    setOpenGymBlocks(blocks || [])
  }, [iso])

  const fetchTrialUses = useCallback(async () => {
    if (!isTrial || !user?.id) {
      setTrialUses(0)
      return
    }

    const { data: oneTimeUses } = await supabase
      .from('class_signups')
      .select('id, classes(is_247)')
      .eq('athlete_id', user.id)

    const { data: recurringUses } = await supabase
      .from('instance_signups')
      .select('id')
      .eq('athlete_id', user.id)

    const classUses = (oneTimeUses || []).filter(s => !s.classes?.is_247).length
    setTrialUses(classUses + (recurringUses || []).length)
  }, [isTrial, user?.id])

  useEffect(() => { fetchClasses() }, [fetchClasses])
  useEffect(() => { fetchOpenGym() }, [fetchOpenGym])
  useEffect(() => { fetchTrialUses() }, [fetchTrialUses])

  useEffect(() => {
    if (isCoach) {
      supabase.from('profiles').select('id, name, avatar_url, membership_type').order('name')
        .then(({ data }) => setAllMembers(data || []))
    }
  }, [isCoach])

  const prevDay = () => { const d = new Date(currentDate); d.setDate(d.getDate() - 1); setCurrentDate(d) }
  const nextDay = () => { const d = new Date(currentDate); d.setDate(d.getDate() + 1); setCurrentDate(d) }
  const goToday = () => setCurrentDate(parseCalendarDate(gymDateKey()))
  const isToday = toISO(currentDate) === gymDateKey()

  // Sign up for a one-time class
  const signup = async (classId) => {
    if (trialExpired) { showToast('Your 10-day free trial window has ended. Activate a membership to keep training.'); return }
    if (isTrial && trialUses >= FREE_TRIAL_CLASS_LIMIT) { showToast('Your free trial classes are used. Upgrade to keep training.'); return }
    if (!canSignUp) { showToast('Your membership does not include class access.'); return }
    if (!profile?.waiver_signed) { showToast('Please sign the liability waiver in your Profile tab first.'); return }

    const { error } = await supabase.from('class_signups').insert({ class_id: classId, athlete_id: user.id })

    if (error) {
      showToast('Already signed up')
    } else {
      const cls = oneTimeClasses.find(c => c.id === classId)
      const className = cls?.title || 'a class'
      const classTime = cls?.start_time
        ? gymTimeLabel(cls.start_time)
        : ''

      await notifyCoach(
        'New Class Signup',
        `${profile?.name || 'An athlete'} signed up for ${className}${classTime ? ` at ${classTime}` : ''}.`
      )

      showToast('Signed up!')
      fetchTrialUses()
      fetchClasses()
    }
  }

  const unsignup = async (classId) => {
    await supabase.from('class_signups').delete().match({ class_id: classId, athlete_id: user.id })
    showToast('Removed'); fetchTrialUses(); fetchClasses()
  }

  // Sign up for a recurring class instance
  const signupInstance = async (instanceId) => {
    if (trialExpired) { showToast('Your 10-day free trial window has ended. Activate a membership to keep training.'); return }
    if (isTrial && trialUses >= FREE_TRIAL_CLASS_LIMIT) { showToast('Your free trial classes are used. Upgrade to keep training.'); return }
    if (!canSignUp) { showToast('Your membership does not include class access.'); return }
    if (!profile?.waiver_signed) { showToast('Please sign the liability waiver in your Profile tab first.'); return }

    const { error } = await supabase.from('instance_signups').insert({ instance_id: instanceId, athlete_id: user.id })

    if (error) {
      showToast('Already signed up')
    } else {
      const cls = recurringClasses.find(c => c.instance?.id === instanceId)
      const className = cls?.title || 'a recurring class'
      const classTime = cls?.recurrence_time || ''

      await notifyCoach(
        'New Class Signup',
        `${profile?.name || 'An athlete'} signed up for ${className}${classTime ? ` at ${classTime}` : ''}.`
      )

      showToast('Signed up!')
      fetchTrialUses()
      fetchClasses()
    }
  }

  const unsignupInstance = async (instanceId) => {
    await supabase.from('instance_signups').delete().match({ instance_id: instanceId, athlete_id: user.id })
    showToast('Removed'); fetchTrialUses(); fetchClasses()
  }

  const checkInClass = async (classId) => {
    const { error } = await supabase.from('class_signups')
      .update({ checkin_time: currentTimeLabel() })
      .match({ class_id: classId, athlete_id: user.id })
    if (error) showToast('Could not check in: ' + error.message)
    else { showToast('Checked in!'); fetchClasses() }
  }

  const checkInInstance = async (instanceId) => {
    const { error } = await supabase.from('instance_signups')
      .update({ checkin_time: currentTimeLabel() })
      .match({ instance_id: instanceId, athlete_id: user.id })
    if (error) showToast('Could not check in: ' + error.message)
    else { showToast('Checked in!'); fetchClasses() }
  }

  const removeFromClass = async (classId, athleteId) => {
    if (!isCoach || !athleteId) return
    await supabase.from('class_signups').delete().match({ class_id: classId, athlete_id: athleteId })
    showToast('Athlete removed')
    fetchClasses()
  }

  const removeFromInstance = async (instanceId, athleteId) => {
    if (!isCoach || !instanceId || !athleteId) return
    await supabase.from('instance_signups').delete().match({ instance_id: instanceId, athlete_id: athleteId })
    showToast('Athlete removed')
    fetchClasses()
  }

  const updateClassTrack = async (classId, track) => {
    if (!isCoach || !classId) return
    const { data, error } = await supabase
      .from('classes')
      .update({ track })
      .eq('id', classId)
      .select('id, track')

    if (error) {
      showToast('Could not update class track: ' + error.message)
      return
    }

    const savedClass = (data || []).find(cls => cls.id === classId)
    if (!savedClass || savedClass.track !== track) {
      showToast('The track did not save. Please try again or check coach permissions.')
      fetchClasses()
      return
    }

    showToast(`Track changed to ${track}`)
    fetchClasses()
  }

  const markClassAttendance = async (classId, athleteId, attended) => {
    if (!isCoach || !classId || !athleteId) return
    const checkin_time = attended ? currentTimeLabel() : null
    const { data, error } = await supabase
      .from('class_signups')
      .update({ checkin_time })
      .match({ class_id: classId, athlete_id: athleteId })
      .select('id')

    if (error) showToast('Could not update attendance: ' + error.message)
    else {
      if (attended && (!data || data.length === 0)) {
        const { error: insertError } = await supabase
          .from('class_signups')
          .insert({ class_id: classId, athlete_id: athleteId, checkin_time })

        if (insertError) {
          showToast('Could not attach attendance to athlete: ' + insertError.message)
          return
        }
      }
      showToast(attended ? 'Marked attended' : 'Attendance removed')
      fetchClasses()
    }
  }

  const markInstanceAttendance = async (instanceId, athleteId, attended) => {
    if (!isCoach || !instanceId || !athleteId) return
    const checkin_time = attended ? currentTimeLabel() : null
    const { data, error } = await supabase
      .from('instance_signups')
      .update({ checkin_time })
      .match({ instance_id: instanceId, athlete_id: athleteId })
      .select('id')

    if (error) showToast('Could not update attendance: ' + error.message)
    else {
      if (attended && (!data || data.length === 0)) {
        const { error: insertError } = await supabase
          .from('instance_signups')
          .insert({ instance_id: instanceId, athlete_id: athleteId, checkin_time })

        if (insertError) {
          showToast('Could not attach attendance to athlete: ' + insertError.message)
          return
        }
      }
      showToast(attended ? 'Marked attended' : 'Attendance removed')
      fetchClasses()
    }
  }

  // Coach manually adds to one-time class
  const manualAdd = async (classId, athleteId) => {
    const { error } = await supabase.from('class_signups').insert({ class_id: classId, athlete_id: athleteId })
    if (error) showToast('Already in class')
    else { showToast('Athlete added'); fetchClasses() }
  }

  // Coach manually adds to recurring instance
  const manualAddInstance = async (instanceId, athleteId) => {
    const { error } = await supabase.from('instance_signups').insert({ instance_id: instanceId, athlete_id: athleteId })
    if (error) showToast('Already in class')
    else { showToast('Athlete added'); fetchClasses() }
  }

  // Coach checks in an athlete manually
  const coachCheckinAthlete = async (athleteId, athleteName) => {
    if (!has247) return
    const { error } = await supabase.from('class_signups').insert({
      class_id: has247.id, athlete_id: athleteId,
      checkin_time: checkinTime, is_247_checkin: true
    })
    if (error) { showToast(athleteName + ' already checked in today') }
    else {
      await supabase.from('notifications').insert({
        message: `${athleteName} was checked in by coach at ${checkinTime}`,
        type: '247_checkin', athlete_id: athleteId
      })
      fetch('/api/push-notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: '🏋️ Open Gym Check-In',
          body: `${athleteName} was checked in by coach at ${checkinTime}`,
          badgeCount: 1,
          tag: 'srb-open-gym'
        })
      }).catch(err => console.error('Push notify error:', err))
      showToast(athleteName + ' checked in!')
      fetchClasses()
    }
  }

  const checkin247 = async () => {
    if (!has247) return
    if (!canUseOpenGymAccess) { showToast('Open gym access is for active Open Gym or Class Access members only.'); return }
    const { error } = await supabase.from('class_signups').insert({
      class_id: has247.id, athlete_id: user.id,
      checkin_time: checkinTime, is_247_checkin: true
    })
    if (error) { showToast('Already checked in today') }
    else {
      await supabase.from('notifications').insert({
        message: `${profile?.name || 'An athlete'} checked in for 24/7 access at ${checkinTime}`,
        type: '247_checkin', athlete_id: user.id
      })
      fetch('/api/push-notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: '🏋️ Open Gym Check-In',
          body: `${profile?.name || 'An athlete'} checked in at ${checkinTime}`,
          badgeCount: 1,
          tag: 'srb-open-gym'
        })
      }).catch(err => console.error('Push notify error:', err))
      showToast(`Checked in for ${checkinTime} — Sarah has been notified!`)
      setShow247(false); fetchClasses()
    }
  }

  const allClasses = [...oneTimeClasses, ...recurringClasses]
  const todaysClassWindows = allClasses.map(cls => classWindow(cls, iso)).filter(Boolean)
  const todaysBlocks = openGymBlocks.filter(block => rowRepeatsToday(block, dayOfWeek, iso))
  const openGymSlotsToday = [...defaultOpenGymSlots(), ...openGymSlots]
    .filter(slot => rowRepeatsToday(slot, dayOfWeek, iso))
    .map(slot => {
      const window = slotWindow(slot)
      const bookings = openGymBookings.filter(booking =>
        slot.defaultSlot ? booking.slot_key === slot.slot_key : booking.slot_id === slot.id
      )
      const conflictingClass = todaysClassWindows.some(classTime => windowsOverlap(window, classTime))
      const conflictingBlock = todaysBlocks.some(block => windowsOverlap(window, slotWindow(block)))
      const unavailableReason = conflictingClass ? 'Class/private coaching time' : conflictingBlock ? 'Blocked by coach' : ''
      return { ...slot, window, bookings, unavailable: conflictingClass || conflictingBlock, unavailableReason }
    })

  const bookOpenGymSlot = async (slot) => {
    if (!canUseOpenGymAccess) { showToast('Open gym access is for active Open Gym or Class Access members only.'); return }
    if (!profile?.waiver_signed) { showToast('Please sign the liability waiver in your Profile tab first.'); return }
    if (slot.unavailable) { showToast('That Open Gym time is not available.'); return }
    if ((slot.bookings?.length || 0) >= (slot.capacity || 1)) { showToast('That Open Gym time is full.'); return }

    const bookingPayload = {
      athlete_id: user.id,
      booking_date: iso
    }

    if (slot.defaultSlot) {
      bookingPayload.slot_key = slot.slot_key
      bookingPayload.slot_start_time = slot.start_time
      bookingPayload.slot_duration_minutes = slot.duration_minutes
    } else {
      bookingPayload.slot_id = slot.id
    }

    const { error } = await supabase.from('open_gym_bookings').insert(bookingPayload)

    if (error) {
      showToast('Already booked')
      return
    }

    await notifyCoach(
      'Open Gym Booked',
      `${profile?.name || 'An athlete'} booked Open Gym for ${timeInputToLabel(slot.start_time)} on ${formatDate(currentDate)}.`
    )

    showToast('Open Gym booked!')
    fetchOpenGym()
  }

  const cancelOpenGymBooking = async (bookingId) => {
    await supabase.from('open_gym_bookings').delete().eq('id', bookingId)
    showToast('Open Gym booking removed')
    fetchOpenGym()
  }

  const removeOpenGymSlot = async (slotId) => {
    if (!isCoach) return
    if (String(slotId).startsWith('default-')) {
      showToast('Default Open Gym times are removed by blocking time.')
      return
    }
    await supabase.from('open_gym_slots').update({ active: false }).eq('id', slotId)
    showToast('Open Gym slot removed')
    fetchOpenGym()
  }

  const removeOpenGymBlock = async (blockId) => {
    if (!isCoach) return
    await supabase.from('open_gym_blocks').update({ active: false }).eq('id', blockId)
    showToast('Open Gym block removed')
    fetchOpenGym()
  }

  const datedClasses = [
    ...oneTimeClasses.map(cls => ({ kind: 'one-time', cls, sortTime: timeInGymMinutes(cls.start_time) })),
    ...recurringClasses.map(cls => ({ kind: 'recurring', cls, sortTime: labelToMinutes(cls.recurrence_time) ?? 9999 }))
  ].sort((a, b) => a.sortTime - b.sortTime)

  const displayedClasses = scheduleView === 'signups'
    ? datedClasses.filter(({ kind, cls }) => kind === 'one-time'
      ? (cls.class_signups || []).some(signupRow => signupRow.athlete_id === user.id)
      : (cls.instance?.instance_signups || []).some(signupRow => signupRow.athlete_id === user.id))
    : datedClasses

  const dateStrip = Array.from({ length: 7 }, (_, index) => {
    const date = new Date(currentDate)
    date.setDate(date.getDate() + index - 3)
    return date
  })

  return (
    <div>
      <div className="schedule-heading">
        <div>
          <div className="schedule-kicker">Reserve Your Training</div>
          <h2>Schedule</h2>
        </div>
        {isCoach && <button className="btn-sm" onClick={() => setShowForm(!showForm)}>{showForm ? 'Close' : 'Add Class'}</button>}
      </div>

      <div className="schedule-tabs" role="tablist" aria-label="Schedule views">
        <button className={scheduleView === 'schedule' ? 'active' : ''} onClick={() => setScheduleView('schedule')}>Schedule</button>
        <button className={scheduleView === 'signups' ? 'active' : ''} onClick={() => setScheduleView('signups')}>My Signups</button>
      </div>

      <div className="schedule-week-strip">
        <button className="schedule-week-arrow" onClick={prevDay} aria-label="Previous day">‹</button>
        {dateStrip.map(date => {
          const selected = toISO(date) === iso
          const today = toISO(date) === gymDateKey()
          return (
            <button key={toISO(date)} className={`schedule-day ${selected ? 'selected' : ''}`} onClick={() => setCurrentDate(date)}>
              <span>{date.toLocaleDateString('en-US', { weekday: 'narrow' })}</span>
              <strong>{date.getDate()}</strong>
              {today && <i />}
            </button>
          )
        })}
        <button className="schedule-week-arrow" onClick={nextDay} aria-label="Next day">›</button>
      </div>

      <div className="schedule-date-heading">
        <span>{formatDate(currentDate)}</span>
        {!isToday && <button onClick={goToday}>Today</button>}
      </div>

      {/* Coach-only 24/7 manual check-in tools */}
      {has247 && isCoach && (
        <div className="class-247">
          <div className="class-247-title">24/7 Access</div>
          {isCoach ? (
            <div>
              <div className="class-247-note">Check in an athlete for 24/7 access.</div>
              {showCoachCheckin
                ? <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginTop: '8px' }}>
                    <select value={checkinTime} onChange={e => setCheckinTime(e.target.value)}
                      style={{ background: 'rgba(245,240,232,0.06)', border: '1px solid var(--border)', borderRadius: '2px', padding: '8px 12px', color: 'var(--bone)', fontFamily: 'Lato, sans-serif', fontSize: '15px', outline: 'none' }}>
                      {CHECKIN_TIMES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {allMembers.map(m => (
                        <button key={m.id} className="btn-ghost" style={{ fontSize: '11px' }}
                          onClick={() => { coachCheckinAthlete(m.id, m.name); setShowCoachCheckin(false) }}>
                          {m.name}
                        </button>
                      ))}
                    </div>
                    <button className="btn-ghost" onClick={() => setShowCoachCheckin(false)}>Cancel</button>
                  </div>
                : <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '8px' }}>
                  <button className="btn-sm" onClick={() => setShowCoachCheckin(true)}>Check In Athlete</button>
                  {!subscribed
                    ? <button className="btn-ghost" style={{ fontSize: '11px' }} onClick={subscribe} disabled={pushLoading}>
                        {pushLoading ? 'Enabling...' : '🔔 Enable Check-In Alerts'}
                      </button>
                    : <button className="btn-ghost" style={{ fontSize: '11px', color: 'var(--moss-light)' }} onClick={unsubscribe}>
                        🔔 Alerts On
                      </button>
                  }
                </div>
              }
            </div>
          ) : (
            <div>
              <div className="class-247-note">Coming in outside of class time? Let Sarah know you're heading in.</div>
              {show247
                ? <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <select value={checkinTime} onChange={e => setCheckinTime(e.target.value)}
                      style={{ background: 'rgba(245,240,232,0.06)', border: '1px solid var(--border)', borderRadius: '2px', padding: '8px 12px', color: 'var(--bone)', fontFamily: 'Lato, sans-serif', fontSize: '15px', outline: 'none' }}>
                      {CHECKIN_TIMES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <button className="btn-sm" onClick={checkin247}>Confirm Check-In</button>
                    <button className="btn-ghost" onClick={() => setShow247(false)}>Cancel</button>
                  </div>
                : <button className="btn-sm" onClick={() => setShow247(true)}>Check In for 24/7 Access</button>
              }
            </div>
          )}
        </div>
      )}

      <OpenGymModule
        isCoach={isCoach}
        user={user}
        canUsePaidClassAccess={canUseOpenGymAccess}
        openGymAvailable={openGymAvailable}
        slots={openGymSlotsToday}
        blocks={openGymBlocks}
        selectedDate={iso}
        showSlotForm={showOpenGymSlotForm}
        setShowSlotForm={setShowOpenGymSlotForm}
        showBlockForm={showOpenGymBlockForm}
        setShowBlockForm={setShowOpenGymBlockForm}
        onBook={bookOpenGymSlot}
        onCancelBooking={cancelOpenGymBooking}
        onRemoveSlot={removeOpenGymSlot}
        onRemoveBlock={removeOpenGymBlock}
        onSaved={fetchOpenGym}
      />

      {isTrial && (
        <div className="panel" style={{ marginBottom: '1rem' }}>
          <div className="panel-title">Free Trial</div>
          <p style={{ fontSize: '14px', color: 'var(--charcoal-light)', lineHeight: 1.6, margin: 0 }}>
            {Math.min(trialUses, FREE_TRIAL_CLASS_LIMIT)} of {FREE_TRIAL_CLASS_LIMIT} trial classes used. Free trials include classes only and expire 10 days after signup; programming and open gym unlock with membership.
          </p>
          {(trialExpired || trialUses >= FREE_TRIAL_CLASS_LIMIT) && (
            <p style={{ fontSize: '14px', color: 'var(--gold-light)', lineHeight: 1.6, margin: '10px 0 0' }}>
              Your trial classes are complete. Upgrade in Profile to keep signing up.
            </p>
          )}
        </div>
      )}

      {isCoach && showForm && <ClassForm onSaved={() => { setShowForm(false); fetchClasses() }} />}

      {loading && <div className="loading">Loading...</div>}

      {!loading && displayedClasses.length === 0 && !showForm && (
        <div className="empty">
          <h3>{scheduleView === 'signups' ? 'No signups this day' : 'No classes today'}</h3>
          <p>{scheduleView === 'signups' ? 'Classes you reserve will appear here.' : isCoach ? 'Add a class, or set up recurring classes for this day.' : 'No classes scheduled for today.'}</p>
        </div>
      )}

      <div className="schedule-class-list">
        {displayedClasses.map(({ kind, cls }) => kind === 'one-time' ? (
          <OneTimeClassCard
            key={`one-${cls.id}`}
            cls={cls}
            user={user}
            isCoach={isCoach}
            allMembers={allMembers}
            onSignup={() => signup(cls.id)}
            onUnsignup={() => unsignup(cls.id)}
            onCheckIn={() => checkInClass(cls.id)}
            onTrackChange={(track) => updateClassTrack(cls.id, track)}
            onManualAdd={(athleteId) => manualAdd(cls.id, athleteId)}
            onRemoveAthlete={(athleteId) => removeFromClass(cls.id, athleteId)}
            onToggleAttendance={(athleteId, attended) => markClassAttendance(cls.id, athleteId, attended)}
            onAthleteClick={isCoach ? (id) => setAthletePanel(id) : null}
          />
        ) : (
          <RecurringClassCard
            key={`recurring-${cls.id}`}
            cls={cls}
            user={user}
            isCoach={isCoach}
            allMembers={allMembers}
            onSignup={() => signupInstance(cls.instance?.id)}
            onUnsignup={() => unsignupInstance(cls.instance?.id)}
            onCheckIn={() => checkInInstance(cls.instance?.id)}
            onTrackChange={(track) => updateClassTrack(cls.id, track)}
            onManualAdd={(athleteId) => manualAddInstance(cls.instance?.id, athleteId)}
            onRemoveAthlete={(athleteId) => removeFromInstance(cls.instance?.id, athleteId)}
            onToggleAttendance={(athleteId, attended) => markInstanceAttendance(cls.instance?.id, athleteId, attended)}
            onAthleteClick={isCoach ? (id) => setAthletePanel(id) : null}
          />
        ))}
      </div>

      {toast && <div className="toast">{toast}</div>}

      {athletePanel && (
        <AthletePanel
          athleteId={athletePanel}
          onClose={() => setAthletePanel(null)}
        />
      )}
      <ScheduleStyles />
    </div>
  )
}

function ScheduleStyles() {
  return (
    <style>{`
      .schedule-heading{display:flex;align-items:flex-end;justify-content:space-between;gap:16px;margin-bottom:18px}
      .schedule-kicker{font-family:'Cinzel',serif;font-size:12px;letter-spacing:3px;text-transform:uppercase;color:var(--rose-light)}
      .schedule-heading h2{font-family:'Cinzel',serif;font-size:30px;letter-spacing:2px;color:var(--gold-light);margin:3px 0 0}
      .schedule-tabs{display:grid;grid-template-columns:1fr 1fr;border-bottom:1px solid var(--border-strong);margin-bottom:8px}
      .schedule-tabs button{background:transparent;border:0;border-bottom:3px solid transparent;color:var(--charcoal-light);font-family:'Cinzel',serif;font-size:15px;letter-spacing:1px;padding:13px;cursor:pointer}
      .schedule-tabs button.active{color:var(--gold-light);border-bottom-color:var(--rose)}
      .schedule-week-strip{display:grid;grid-template-columns:40px repeat(7,minmax(38px,1fr)) 40px;align-items:center;gap:4px;padding:10px 0 14px;border-bottom:1px solid var(--border);margin-bottom:0}
      .schedule-week-arrow{width:38px;height:44px;background:transparent;border:0;color:var(--gold-light);font-size:28px;cursor:pointer}
      .schedule-day{position:relative;height:62px;min-width:0;border:1px solid transparent;border-radius:4px;background:transparent;color:var(--charcoal-light);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1px;cursor:pointer}
      .schedule-day span{font-size:11px;text-transform:uppercase}.schedule-day strong{font-family:'Cinzel',serif;font-size:18px;font-weight:400;color:var(--bone)}
      .schedule-day.selected{background:var(--rose-dark);border-color:var(--rose);color:var(--bone)}.schedule-day.selected strong{color:#fff}
      .schedule-day i{position:absolute;bottom:4px;width:4px;height:4px;border-radius:50%;background:var(--gold-light)}
      .schedule-date-heading{display:flex;align-items:center;justify-content:space-between;background:rgba(255,248,236,.07);border-bottom:1px solid var(--border);padding:11px 14px;margin-bottom:12px;color:var(--gold-light);font-family:'Cinzel',serif;font-size:15px;letter-spacing:1px}
      .schedule-date-heading button{border:0;background:transparent;color:var(--rose-light);font-size:12px;text-transform:uppercase;letter-spacing:1px;cursor:pointer}
      .schedule-class-list{display:flex;flex-direction:column;gap:8px;margin-bottom:18px}
      .schedule-class-card{position:relative;margin:0;border-color:var(--border-strong);background:rgba(255,248,236,.09);padding-left:1.55rem}
      .schedule-class-card::before{content:'';position:absolute;left:0;top:0;bottom:0;width:5px;background:var(--gold-dark)}
      .schedule-class-card.reserved::before{background:var(--moss-light)}
      .schedule-class-card.reserved::after{content:'Reserved';position:absolute;top:0;left:0;background:var(--moss);color:#fff;font-size:9px;letter-spacing:1px;text-transform:uppercase;padding:2px 7px}
      .schedule-class-card.reserved{padding-top:1.8rem}
      .schedule-class-card .class-card-header{align-items:center}.schedule-class-card .class-title{font-size:22px}
      .schedule-signup-button{width:48px;height:48px;flex-shrink:0;border-radius:50%;border:2px solid var(--gold);background:transparent;color:var(--gold-light);font-size:32px;line-height:1;cursor:pointer}
      .schedule-signup-button:disabled{opacity:.45;cursor:not-allowed;font-size:11px}
      .coach-track-select{display:inline-flex;align-items:center;gap:7px;color:var(--charcoal-light);font-size:11px;text-transform:uppercase;letter-spacing:1px}
      .coach-track-select select{max-width:220px;background:rgba(255,248,236,.12);border:1px solid var(--border-strong);border-radius:3px;color:var(--bone);font-size:13px;padding:6px 8px;cursor:pointer}
      .coach-track-select option{background:var(--charcoal);color:var(--bone)}
      @media(max-width:640px){.schedule-heading h2{font-size:26px}.schedule-week-strip{grid-template-columns:28px repeat(7,minmax(34px,1fr)) 28px;gap:1px}.schedule-week-arrow{width:28px}.schedule-day{height:56px;padding:0}.schedule-day strong{font-size:16px}.schedule-class-card .class-card-header{gap:10px}.schedule-class-card .class-card-header>div:last-child{flex-wrap:wrap;justify-content:flex-end}.schedule-class-card .class-title{font-size:20px}.schedule-signup-button{width:44px;height:44px}.schedule-date-heading{font-size:13px}}
    `}</style>
  )
}

function OneTimeClassCard({ cls, user, isCoach, allMembers, onSignup, onUnsignup, onCheckIn, onTrackChange, onManualAdd, onRemoveAthlete, onToggleAttendance, onAthleteClick }) {
  const isSignedUp = cls.class_signups?.some(s => s.athlete_id === user.id)
  const mySignup = cls.class_signups?.find(s => s.athlete_id === user.id)
  const spots = cls.capacity - (cls.class_signups?.length || 0)
  const full = spots <= 0
  const dt = new Date(cls.start_time)

  return (
    <div className={`class-card schedule-class-card ${isSignedUp ? 'reserved' : ''}`}>
      <div className="class-card-header">
        <div className="class-title">{cls.title}</div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {isSignedUp ? (
            <>
              <button className="btn-ghost" onClick={onUnsignup}>Cancel</button>
              {mySignup?.checkin_time
                ? <button className="btn-moss" disabled>Checked In</button>
                : <button className="btn-sm" onClick={onCheckIn}>Check In</button>}
            </>
          ) : <button className="schedule-signup-button" onClick={onSignup} disabled={full} aria-label={`Sign up for ${cls.title}`}>{full ? 'Full' : '+'}</button>
          }
        </div>
      </div>
      <div className="class-meta">
        <span>{gymTimeLabel(dt)}</span>
        <span>{cls.duration_minutes} min</span>
        {isCoach
          ? <CoachTrackSelect track={cls.track} onChange={onTrackChange} />
          : <ClassTrackBadge track={cls.track} />}
      </div>
      {cls.description && <p style={{ fontSize: '14px', color: 'var(--charcoal-light)', marginBottom: '10px' }}>{cls.description}</p>}
      <ClassFooter signups={cls.class_signups || []} spots={spots} isSignedUp={isSignedUp} isCoach={isCoach} allMembers={allMembers} onManualAdd={onManualAdd} onRemoveAthlete={onRemoveAthlete} onToggleAttendance={onToggleAttendance} onAthleteClick={onAthleteClick} />
    </div>
  )
}

function RecurringClassCard({ cls, user, isCoach, allMembers, onSignup, onUnsignup, onCheckIn, onTrackChange, onManualAdd, onRemoveAthlete, onToggleAttendance, onAthleteClick }) {
  const instance = cls.instance
  const signups = instance?.instance_signups || []
  const isSignedUp = signups.some(s => s.athlete_id === user.id)
  const mySignup = signups.find(s => s.athlete_id === user.id)
  const spots = cls.capacity - signups.length
  const full = spots <= 0

  return (
    <div className={`class-card schedule-class-card ${isSignedUp ? 'reserved' : ''}`}>
      <div className="class-card-header">
        <div>
          <div className="class-title">{cls.title}</div>
          <div style={{ fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--gold-dark)', marginTop: '4px' }}>
            Recurring · {(cls.recurrence_days || '').split(',').join(' · ')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {isSignedUp ? (
            <>
              <button className="btn-ghost" onClick={onUnsignup}>Cancel</button>
              {mySignup?.checkin_time
                ? <button className="btn-moss" disabled>Checked In</button>
                : <button className="btn-sm" onClick={onCheckIn}>Check In</button>}
            </>
          ) : <button className="schedule-signup-button" onClick={onSignup} disabled={full || !instance} aria-label={`Sign up for ${cls.title}`}>{full ? 'Full' : '+'}</button>
          }
        </div>
      </div>
      <div className="class-meta">
        <span>{cls.recurrence_time || '—'}</span>
        <span>{cls.duration_minutes} min</span>
        <span style={{ color: 'var(--gold)', fontSize: '11px' }}>Recurring</span>
        {isCoach
          ? <CoachTrackSelect track={cls.track} onChange={onTrackChange} />
          : <ClassTrackBadge track={cls.track} />}
      </div>
      {cls.description && <p style={{ fontSize: '14px', color: 'var(--charcoal-light)', marginBottom: '10px' }}>{cls.description}</p>}
      <ClassFooter signups={signups} spots={spots} isSignedUp={isSignedUp} isCoach={isCoach} allMembers={allMembers} onManualAdd={onManualAdd} onRemoveAthlete={onRemoveAthlete} onToggleAttendance={onToggleAttendance} onAthleteClick={onAthleteClick} />
    </div>
  )
}

function CoachTrackSelect({ track, onChange }) {
  return (
    <label className="coach-track-select">
      <span>Track</span>
      <select value={track || 'All Tracks'} onChange={event => onChange(event.target.value)}>
        {CLASS_TRACKS.map(option => <option key={option} value={option}>{option}</option>)}
      </select>
    </label>
  )
}

function ClassFooter({ signups, spots, isSignedUp, isCoach, allMembers, onManualAdd, onRemoveAthlete, onToggleAttendance, onAthleteClick }) {
  return (
    <>
      <div className="class-spots">
        {isSignedUp && <span style={{ color: 'var(--moss-light)', marginRight: '12px' }}>✓ You're in</span>}
        {spots > 0 ? `${spots} spot${spots !== 1 ? 's' : ''} remaining` : 'Class full'}
      </div>
      {signups.length > 0 && isCoach && (
        <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {signups.map((s, i) => {
            const attended = Boolean(s.checkin_time)
            return (
              <span key={i}
                onClick={() => { if (onAthleteClick && s.athlete_id) onAthleteClick(s.athlete_id) }}
                style={{ fontSize: '12px', color: onAthleteClick ? 'var(--gold-light)' : 'var(--charcoal-light)', background: attended ? 'rgba(107,115,85,0.18)' : 'rgba(245,240,232,0.04)', border: attended ? '1px solid var(--moss)' : '1px solid var(--border)', borderRadius: '2px', padding: '3px 8px', cursor: onAthleteClick ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <span>{s.profiles?.name || 'Athlete'}</span>
                {attended && <span style={{ color: 'var(--moss-light)', fontSize: '11px' }}>✓ {s.checkin_time}</span>}
                <button
                  type="button"
                  aria-label={attended ? `Remove attendance for ${s.profiles?.name || 'athlete'}` : `Mark ${s.profiles?.name || 'athlete'} attended`}
                  onClick={(e) => { e.stopPropagation(); onToggleAttendance?.(s.athlete_id, !attended) }}
                  style={{ background: 'transparent', border: 'none', color: attended ? 'var(--moss-light)' : 'var(--gold-light)', cursor: 'pointer', fontSize: '12px', lineHeight: 1, padding: '0 2px' }}
                >
                  {attended ? 'Undo' : 'Check in'}
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${s.profiles?.name || 'athlete'} from class`}
                  onClick={(e) => { e.stopPropagation(); onRemoveAthlete?.(s.athlete_id) }}
                  style={{ background: 'transparent', border: 'none', color: 'var(--rose-light)', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: '0 0 1px' }}
                >
                  ×
                </button>
              </span>
            )
          })}
        </div>
      )}
      {signups.length > 0 && !isCoach && isSignedUp && (
        <div style={{ fontSize: '12px', color: 'var(--charcoal-light)', marginTop: '6px' }}>
          {signups.length} athlete{signups.length !== 1 ? 's' : ''} signed up
        </div>
      )}
      {isCoach && (
        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
          <ManualAddSearch allMembers={allMembers} signups={signups} onManualAdd={onManualAdd} />
        </div>
      )}
    </>
  )
}

function OpenGymModule({ isCoach, user, canUsePaidClassAccess, openGymAvailable, slots, blocks, selectedDate, showSlotForm, setShowSlotForm, showBlockForm, setShowBlockForm, onBook, onCancelBooking, onRemoveSlot, onRemoveBlock, onSaved }) {
  const [showPicker, setShowPicker] = useState(false)
  const availableSlots = slots.filter(slot => {
    const spots = Math.max((slot.capacity || 1) - (slot.bookings?.length || 0), 0)
    const myBooking = slot.bookings?.find(booking => booking.athlete_id === user.id)
    return myBooking || (!slot.unavailable && spots > 0)
  })
  const myBookings = slots.flatMap(slot =>
    (slot.bookings || [])
      .filter(booking => booking.athlete_id === user.id)
      .map(booking => ({ ...booking, slot }))
  )

  if (!openGymAvailable) {
    return (
      <div className="class-247">
        <div className="class-247-title">Open Gym</div>
          <div className="class-247-note">
          Open Gym scheduling needs the new database tables installed. Until then, the older check-in flow above still works.
        </div>
      </div>
    )
  }

  return (
    <div className="class-247">
      <div className="class-card-header">
        <div>
          <div className="class-247-title">Open Gym</div>
          <div className="class-247-note">Open Gym is available 24/7 unless it overlaps class time or a coach block.</div>
        </div>
        {isCoach && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button className="btn-sm" onClick={() => setShowBlockForm(!showBlockForm)}>Block Time</button>
            <button className="btn-ghost" onClick={() => setShowSlotForm(!showSlotForm)}>Extra Slot</button>
          </div>
        )}
      </div>

      {isCoach && showSlotForm && (
        <OpenGymSlotForm
          onSaved={() => { setShowSlotForm(false); onSaved() }}
          onCancel={() => setShowSlotForm(false)}
        />
      )}

      {isCoach && showBlockForm && (
        <OpenGymBlockForm
          onSaved={() => { setShowBlockForm(false); onSaved() }}
          onCancel={() => setShowBlockForm(false)}
        />
      )}

      {slots.length === 0 && (
        <div className="empty" style={{ margin: '0.75rem 0 0', padding: '1rem' }}>
          <h3>No Open Gym windows today</h3>
          <p>{isCoach ? 'All default Open Gym windows are blocked by classes or coach blocks.' : 'No Open Gym times are available today.'}</p>
        </div>
      )}

      {!isCoach && (
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="btn-sm" onClick={() => setShowPicker(true)} disabled={!canUsePaidClassAccess || availableSlots.length === 0}>
            Check In for Open Gym
          </button>
          {myBookings.map(booking => (
            <button key={booking.id} className="btn-ghost" onClick={() => onCancelBooking(booking.id)}>
              Cancel {timeInputToLabel(booking.slot.start_time)}
            </button>
          ))}
        </div>
      )}

      {isCoach && (
        <div className="open-gym-coach-summary">
          {slots.filter(slot => !slot.unavailable && (slot.bookings || []).length > 0).length === 0 && (
            <div className="open-gym-meta">No Open Gym bookings yet today.</div>
          )}
          {slots.filter(slot => !slot.unavailable && (slot.bookings || []).length > 0).map(slot => (
            <div key={slot.id} className="open-gym-slot">
              <div>
                <div className="open-gym-time">{timeInputToLabel(slot.start_time)} · {slot.duration_minutes} min</div>
                <div className="open-gym-bookings">
                  {slot.bookings.map(booking => (
                    <span key={booking.id}>
                      {booking.profiles?.name || 'Athlete'}
                      <button type="button" onClick={() => onCancelBooking(booking.id)}>×</button>
                    </span>
                  ))}
                </div>
              </div>
              {!slot.defaultSlot && <button className="btn-ghost" style={{ fontSize: '11px', color: 'var(--rose-light)' }} onClick={() => onRemoveSlot(slot.id)}>Remove</button>}
            </div>
          ))}
          <OpenGymBlocksSchedule blocks={blocks} selectedDate={selectedDate} onRemove={onRemoveBlock} />
        </div>
      )}

      {showPicker && (
        <OpenGymPickerModal
          slots={availableSlots}
          user={user}
          canUsePaidClassAccess={canUsePaidClassAccess}
          onBook={async (slot) => {
            await onBook(slot)
            setShowPicker(false)
          }}
          onCancelBooking={onCancelBooking}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  )
}

function OpenGymBlocksSchedule({ blocks, selectedDate, onRemove }) {
  const activeBlocks = (blocks || []).filter(block => block.active !== false)
  const recurringBlocks = activeBlocks.filter(block => !block.block_date)
  const datedBlocks = activeBlocks
    .filter(block => block.block_date)
    .sort((a, b) => a.block_date.localeCompare(b.block_date) || a.start_time.localeCompare(b.start_time))

  const blockTime = block => {
    const endMinutes = inputTimeToMinutes(block.start_time) + (block.duration_minutes || 60)
    return `${timeInputToLabel(block.start_time)} - ${timeInputToLabel(minutesToInputTime(endMinutes % 1440))}`
  }

  return (
    <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid var(--border)' }}>
      <div className="class-247-title" style={{ fontSize: '15px' }}>Blocked Times</div>
      <div className="class-247-note">Your recurring weekly blocks and one-time closures are shown here.</div>

      {activeBlocks.length === 0 && <div className="open-gym-meta" style={{ marginTop: '10px' }}>No active Open Gym blocks.</div>}

      {recurringBlocks.length > 0 && (
        <div className="open-gym-block-week">
          {DAYS.map((day, index) => {
            const dayBlocks = recurringBlocks
              .filter(block => (block.recurrence_days || '').split(',').map(value => value.trim()).includes(day))
              .sort((a, b) => a.start_time.localeCompare(b.start_time))
            return (
              <div key={day} className="open-gym-block-day">
                <div className="open-gym-block-day-name">{['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][index]}</div>
                <div className="open-gym-block-day-times">
                  {dayBlocks.length === 0 && <span className="open-gym-meta">Open all day</span>}
                  {dayBlocks.map(block => (
                    <span key={block.id} className="open-gym-block-chip">
                      <strong>{blockTime(block)}</strong>
                      {block.reason && <small>{block.reason}</small>}
                      <button type="button" onClick={() => onRemove(block.id)} aria-label={`Remove ${day} block`}>×</button>
                    </span>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {datedBlocks.length > 0 && (
        <div style={{ marginTop: '12px' }}>
          <div className="open-gym-block-section-label">One-Time Blocks</div>
          {datedBlocks.map(block => {
            const dateLabel = new Date(`${block.block_date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
            return (
              <div key={block.id} className={`open-gym-slot${block.block_date === selectedDate ? ' selected-block' : ''}`}>
                <div>
                  <div className="open-gym-time">{dateLabel} · {blockTime(block)}</div>
                  {block.reason && <div className="open-gym-notes">{block.reason}</div>}
                </div>
                <button className="btn-ghost" style={{ fontSize: '11px', color: 'var(--rose-light)' }} onClick={() => onRemove(block.id)}>Remove</button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function OpenGymPickerModal({ slots, user, canUsePaidClassAccess, onBook, onCancelBooking, onClose }) {
  return (
    <div className="modal-wrap" onClick={e => { if (e.target.className === 'modal-wrap') onClose() }}>
      <div className="modal" style={{ maxWidth: '560px' }}>
        <div className="modal-head">
          <div>
            <div className="modal-title">Open Gym</div>
            <div className="modal-sub">Choose an available one-hour window.</div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {slots.length === 0 && <p className="no-data">No Open Gym times are available today.</p>}
          <div className="open-gym-slot-list">
            {slots.map(slot => {
              const myBooking = slot.bookings?.find(booking => booking.athlete_id === user.id)
              const spots = Math.max((slot.capacity || 1) - (slot.bookings?.length || 0), 0)
              const endMinutes = inputTimeToMinutes(slot.start_time) + (slot.duration_minutes || 60)
              return (
                <div key={slot.id} className="open-gym-slot">
                  <div>
                    <div className="open-gym-time">{timeInputToLabel(slot.start_time)} - {timeInputToLabel(minutesToInputTime(endMinutes % 1440))}</div>
                    <div className="open-gym-meta">{spots} spot{spots !== 1 ? 's' : ''} open</div>
                    {slot.notes && <div className="open-gym-notes">{slot.notes}</div>}
                  </div>
                  {myBooking
                    ? <button className="btn-ghost" onClick={() => onCancelBooking(myBooking.id)}>Cancel</button>
                    : <button className="btn-sm" onClick={() => onBook(slot)} disabled={!canUsePaidClassAccess}>Check In</button>
                  }
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

function OpenGymSlotForm({ onSaved, onCancel }) {
  const [time, setTime] = useState('10:00')
  const [duration, setDuration] = useState(60)
  const [capacity, setCapacity] = useState(1)
  const [notes, setNotes] = useState('')
  const [recurDays, setRecurDays] = useState([])
  const [loading, setLoading] = useState(false)

  const toggleDay = (day) => setRecurDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day])
  const sortedDays = [...recurDays].sort((a, b) => DAYS.indexOf(a) - DAYS.indexOf(b))

  const save = async () => {
    if (sortedDays.length === 0) return
    setLoading(true)
    await supabase.from('open_gym_slots').insert({
      start_time: time,
      duration_minutes: parseInt(duration, 10),
      capacity: parseInt(capacity, 10),
      recurrence_days: sortedDays.join(','),
      notes: notes.trim(),
      active: true
    })
    setLoading(false)
    onSaved()
  }

  return (
    <div className="open-gym-form">
      <div className="two-col">
        <div className="field"><label>Start Time</label><input type="time" value={time} onChange={e => setTime(e.target.value)} /></div>
        <div className="field"><label>Duration</label><input type="number" value={duration} onChange={e => setDuration(e.target.value)} /></div>
      </div>
      <div className="field"><label>Capacity</label><input type="number" value={capacity} onChange={e => setCapacity(e.target.value)} /></div>
      <div className="field"><label>Repeats On</label><DayPicker selected={recurDays} onToggle={toggleDay} /></div>
      <div className="field"><label>Notes</label><input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional: Open platform only, no dropping, etc." /></div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button className="btn-sm" onClick={save} disabled={loading || sortedDays.length === 0}>{loading ? 'Saving...' : 'Save Open Gym Slot'}</button>
        <button className="btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}

function OpenGymBlockForm({ onSaved, onCancel }) {
  const today = gymDateKey()
  const [isRecurring, setIsRecurring] = useState(false)
  const [date, setDate] = useState(today)
  const [time, setTime] = useState('10:00')
  const [duration, setDuration] = useState(60)
  const [reason, setReason] = useState('Private coaching')
  const [recurDays, setRecurDays] = useState([])
  const [loading, setLoading] = useState(false)

  const toggleDay = (day) => setRecurDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day])
  const sortedDays = [...recurDays].sort((a, b) => DAYS.indexOf(a) - DAYS.indexOf(b))

  const save = async () => {
    if (isRecurring && sortedDays.length === 0) return
    setLoading(true)
    await supabase.from('open_gym_blocks').insert({
      block_date: isRecurring ? null : date,
      start_time: time,
      duration_minutes: parseInt(duration, 10),
      recurrence_days: isRecurring ? sortedDays.join(',') : null,
      reason: reason.trim() || 'Blocked',
      active: true
    })
    setLoading(false)
    onSaved()
  }

  return (
    <div className="open-gym-form">
      <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <button className={!isRecurring ? 'btn-sm' : 'btn-ghost'} onClick={() => setIsRecurring(false)}>One-Time Block</button>
        <button className={isRecurring ? 'btn-sm' : 'btn-ghost'} onClick={() => setIsRecurring(true)}>Recurring Block</button>
      </div>
      {!isRecurring && <div className="field"><label>Date</label><input type="date" value={date} onChange={e => setDate(e.target.value)} /></div>}
      {isRecurring && <div className="field"><label>Repeats On</label><DayPicker selected={recurDays} onToggle={toggleDay} /></div>}
      <div className="two-col">
        <div className="field"><label>Start Time</label><input type="time" value={time} onChange={e => setTime(e.target.value)} /></div>
        <div className="field"><label>Duration</label><input type="number" value={duration} onChange={e => setDuration(e.target.value)} /></div>
      </div>
      <div className="field"><label>Reason</label><input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="Private coaching, client session, cleaning..." /></div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button className="btn-sm" onClick={save} disabled={loading || (isRecurring && sortedDays.length === 0)}>{loading ? 'Saving...' : 'Save Block'}</button>
        <button className="btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}

function DayPicker({ selected, onToggle }) {
  return (
    <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
      {DAYS.map((day, i) => (
        <button
          key={day}
          onClick={() => onToggle(day)}
          style={{
            width: '40px', height: '40px', borderRadius: '50%', border: '1px solid',
            borderColor: selected.includes(day) ? 'var(--rose)' : 'var(--border)',
            background: selected.includes(day) ? 'rgba(162,92,107,0.3)' : 'transparent',
            color: selected.includes(day) ? 'var(--rose-light)' : 'var(--charcoal-light)',
            cursor: 'pointer', fontSize: '12px', fontFamily: 'Lato, sans-serif',
            transition: 'all 0.15s'
          }}
        >
          {DAY_LABELS[i]}
        </button>
      ))}
    </div>
  )
}

function ClassForm({ onSaved }) {
  const [title, setTitle] = useState('')
  const [desc, setDesc] = useState('')
  const [date, setDate] = useState('')
  const [time, setTime] = useState('07:00')
  const [duration, setDuration] = useState(60)
  const [capacity, setCapacity] = useState(12)
  const [isRecurring, setIsRecurring] = useState(false)
  const [recurDays, setRecurDays] = useState([])
  const [is247, setIs247] = useState(false)
  const [track, setTrack] = useState('All Tracks')
  const [loading, setLoading] = useState(false)

  const toggleDay = (day) => {
    setRecurDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day])
  }

  // Sort days in week order
  const sortedDays = [...recurDays].sort((a, b) => DAYS.indexOf(a) - DAYS.indexOf(b))

  const save = async () => {
    setLoading(true)

    if (is247) {
      await supabase.from('classes').insert({ title, description: desc, is_247: true, duration_minutes: parseInt(duration), capacity: parseInt(capacity) })
    } else if (isRecurring) {
      if (recurDays.length === 0) { setLoading(false); return }
      // Format time for display e.g. "7:00 AM"
      const [h, m] = time.split(':')
      const hr = parseInt(h)
      const displayTime = `${hr > 12 ? hr - 12 : hr === 0 ? 12 : hr}:${m} ${hr >= 12 ? 'PM' : 'AM'}`
      await supabase.from('classes').insert({
        title, description: desc,
        is_recurring: true,
        recurrence_days: sortedDays.join(','),
        recurrence_time: displayTime,
        duration_minutes: parseInt(duration),
        capacity: parseInt(capacity),
        track,
        is_247: false
      })
    } else {
      const startTime = new Date(`${date}T${time}`).toISOString()
      await supabase.from('classes').insert({ title, description: desc, start_time: startTime, duration_minutes: parseInt(duration), capacity: parseInt(capacity), track, is_recurring: false, is_247: false })
    }

    setLoading(false)
    onSaved()
  }

  return (
    <div className="panel" style={{ marginBottom: '1.5rem' }}>
      <div className="panel-title">Add Class</div>

      <div className="field"><label>Class Name</label><input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Babes Who Fight Bears" /></div>
      <div className="field"><label>Description</label><input type="text" value={desc} onChange={e => setDesc(e.target.value)} placeholder="Optional notes for members" /></div>

      {!is247 && (
        <div className="field">
          <label>Track</label>
          <select value={track} onChange={e => setTrack(e.target.value)}>
            {CLASS_TRACKS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      )}

      {/* Type selector */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <button className={!isRecurring && !is247 ? 'btn-sm' : 'btn-ghost'} onClick={() => { setIsRecurring(false); setIs247(false) }}>One-Time</button>
        <button className={isRecurring ? 'btn-sm' : 'btn-ghost'} onClick={() => { setIsRecurring(true); setIs247(false) }}>Recurring</button>
        <button className={is247 ? 'btn-sm' : 'btn-ghost'} onClick={() => { setIs247(true); setIsRecurring(false) }}>24/7 Access</button>
      </div>

      {/* One-time fields */}
      {!isRecurring && !is247 && (
        <div className="two-col">
          <div className="field"><label>Date</label><input type="date" value={date} onChange={e => setDate(e.target.value)} /></div>
          <div className="field"><label>Time</label><input type="time" value={time} onChange={e => setTime(e.target.value)} /></div>
        </div>
      )}

      {/* Recurring fields */}
      {isRecurring && (
        <>
          <div className="field">
            <label>Time</label>
            <input type="time" value={time} onChange={e => setTime(e.target.value)} />
          </div>
          <div className="field">
            <label>Repeats On</label>
            <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
              {DAYS.map((day, i) => (
                <button
                  key={day}
                  onClick={() => toggleDay(day)}
                  style={{
                    width: '40px', height: '40px', borderRadius: '50%', border: '1px solid',
                    borderColor: recurDays.includes(day) ? 'var(--rose)' : 'var(--border)',
                    background: recurDays.includes(day) ? 'rgba(162,92,107,0.3)' : 'transparent',
                    color: recurDays.includes(day) ? 'var(--rose-light)' : 'var(--charcoal-light)',
                    cursor: 'pointer', fontSize: '12px', fontFamily: 'Lato, sans-serif',
                    transition: 'all 0.15s'
                  }}
                >
                  {DAY_LABELS[i]}
                </button>
              ))}
            </div>
            {recurDays.length > 0 && (
              <p style={{ fontSize: '12px', color: 'var(--moss-light)', marginTop: '8px' }}>
                Repeats every {sortedDays.join(', ')}
              </p>
            )}
          </div>
        </>
      )}

      {/* Capacity and duration */}
      {!is247 && (
        <div className="two-col">
          <div className="field"><label>Duration (min)</label><input type="number" value={duration} onChange={e => setDuration(e.target.value)} /></div>
          <div className="field"><label>Capacity</label><input type="number" value={capacity} onChange={e => setCapacity(e.target.value)} /></div>
        </div>
      )}

      <button className="btn-primary" onClick={save} disabled={loading || (isRecurring && recurDays.length === 0)}>
        {loading ? 'Saving...' : 'Save Class'}
      </button>
    </div>
  )
}

function ManualAddSearch({ allMembers, signups, onManualAdd }) {
  const [search, setSearch] = React.useState('')
  const [open, setOpen] = React.useState(false)

  const available = allMembers.filter(m =>
    !signups.some(s => s.athlete_id === m.id) &&
    (!search.trim() || m.name?.toLowerCase().includes(search.toLowerCase()))
  )

  return (
    <div>
      <div style={{ fontSize: '12px', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--charcoal-light)', marginBottom: '8px' }}>Manually Add Athlete</div>
      {!open
        ? <button className="btn-ghost" style={{ fontSize: '11px' }} onClick={() => setOpen(true)}>+ Add Athlete</button>
        : <div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search athletes..."
                autoFocus
                style={{ flex: 1, background: 'rgba(245,240,232,0.06)', border: '1px solid var(--border)', borderRadius: '2px', padding: '6px 10px', color: 'var(--bone)', fontFamily: 'Lato, sans-serif', fontSize: '13px', outline: 'none' }}
              />
              <button className="btn-ghost" style={{ fontSize: '11px' }} onClick={() => { setOpen(false); setSearch('') }}>Cancel</button>
            </div>
            <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '2px' }}>
              {available.length === 0 && (
                <div style={{ padding: '10px 12px', fontSize: '13px', color: 'var(--charcoal-light)' }}>
                  {search ? 'No athletes found' : 'All athletes already signed up'}
                </div>
              )}
              {available.map(m => (
                <div key={m.id}
                  onClick={() => { onManualAdd(m.id); setOpen(false); setSearch('') }}
                  style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: '13px', color: 'var(--bone)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: 'var(--gold-dark)' }}>+</span> {m.name}
                </div>
              ))}
            </div>
          </div>
      }
    </div>
  )
}
